// PB-1990: single source of truth for thousands separators in the portal.
// Extracted from util.js so it can be unit-tested directly — util.js pulls in
// dateformat (ESM) which Jest cannot transform, which is why every test that
// touches util.js has to mock it wholesale.
//
// Display only. Never run a value through these before putting it in an API
// payload, in component state that feeds an <input>, or through parseFloat —
// the commas will corrupt it.

const groupIntegerPart = (intPart) =>
  intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

// Formats a numeric string with thousands separators, trimming trailing zeros
// unless asked to keep them. This is the long-standing portal behaviour behind
// showAmount/showPrice/showRoundAmount — do not change its rounding or its
// trailing-zero handling.
export const formatNumberWithCommas = (value, showTrailingZeros = false) => {
  const [integerPart, decimalPart] = value.toString().split('.');
  const formattedInteger = groupIntegerPart(integerPart);
  if (decimalPart) {
    if (showTrailingZeros) {
      // Keep trailing zeros
      return `${formattedInteger}.${decimalPart}`;
    } else {
      // Trim trailing zeros directly in the decimal part
      const trimmedDecimal = decimalPart.replace(/0+$/, '').replace(/\.$/, '');
      return trimmedDecimal
        ? `${formattedInteger}.${trimmedDecimal}`
        : formattedInteger;
    }
  }
  return formattedInteger;
};

// Groups the integer part and leaves everything after the decimal point exactly
// as given — no rounding, no truncation, no trailing-zero trimming.
//
// Use this on values that have ALREADY been rounded/truncated upstream
// (truncateAmount, roundDownAmount, toFixed, raw API strings), so that adding
// separators cannot silently change the number of decimals a client sees.
// Money in this portal truncates rather than rounds; this helper preserves that.
//
// Anything that is not a plain number is returned untouched, so placeholders
// like '-' and 'N/A' pass through safely.
export const groupInteger = (value) => {
  if (value === null || value === undefined || value === '') return value;

  const str = String(value);

  // Leading sign and trailing non-numeric suffix (e.g. ' BTC') are preserved.
  const match = str.match(/^(-?)(\d*)(\.\d*)?(.*)$/);
  if (!match) return value;

  const [, sign, intPart, decPart = '', rest = ''] = match;

  // No digits before the decimal point and none after a sign means this was
  // never a number we should touch ('-', 'N/A', '1,234' with commas already).
  if (!intPart && !decPart) return value;

  return `${sign}${groupIntegerPart(intPart)}${decPart}${rest}`;
};
